import React, { useState, useEffect } from 'react';
import { FaTimes, FaTrash, FaCheck, FaHistory, FaExclamationTriangle } from 'react-icons/fa';

const RiskDetailsPanel = ({
    risk,
    members,
    isAdmin,
    onClose,
    onSave,
    onDelete,
    onRemoveSignOff,
    onSignOff,
    currentUserEmail
}) => {
    const isNewRisk = !risk;
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        impact: '',
        assignedToEmail: '',
        assignedToName: ''
    });
    const [showSignOffModal, setShowSignOffModal] = useState(false);
    const [acknowledgement, setAcknowledgement] = useState('');
    const [showRemoveSignOffModal, setShowRemoveSignOffModal] = useState(false);
    const [removalReason, setRemovalReason] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (risk) {
            setFormData({
                title: risk.title || '',
                description: risk.description || '',
                impact: risk.impact || '',
                assignedToEmail: risk.assignedToEmail || '',
                assignedToName: risk.assignedToName || ''
            });
        }
    }, [risk]);

    // Check if form has unsaved changes
    const hasChanges = risk ? (
        formData.title !== (risk.title || '') ||
        formData.description !== (risk.description || '') ||
        formData.impact !== (risk.impact || '') ||
        formData.assignedToEmail !== (risk.assignedToEmail || '')
    ) : true; // New risk always has "changes"

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAssigneeChange = (e) => {
        const email = e.target.value;
        if (!email) {
            setFormData(prev => ({
                ...prev,
                assignedToEmail: '',
                assignedToName: ''
            }));
        } else {
            const member = members.find(m => m.email === email);
            if (member) {
                setFormData(prev => ({
                    ...prev,
                    assignedToEmail: member.email,
                    assignedToName: member.name
                }));
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim() || !formData.description.trim()) {
            alert('Title and description are required');
            return;
        }
        setSaving(true);
        try {
            await onSave(formData);
        } finally {
            setSaving(false);
        }
    };

    const handleSignOff = () => {
        if (acknowledgement.trim().length < 10) {
            alert('Acknowledgement must be at least 10 characters');
            return;
        }
        onSignOff(risk.riskId, acknowledgement.trim());
        setShowSignOffModal(false);
        setAcknowledgement('');
    };

    const handleRemoveSignOff = () => {
        if (!removalReason.trim()) {
            alert('Please provide a reason for removing the sign-off');
            return;
        }
        onRemoveSignOff(risk.riskId, removalReason.trim());
        setShowRemoveSignOffModal(false);
        setRemovalReason('');
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Members are DynamoDB clients (from project-clients endpoint)
    // Format: { name, email, leadId, clientRole }
    const assignableMembers = members.filter(m => m.email);

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-[60]" onClick={onClose}>
            <div className={`bg-white rounded-lg shadow-xl max-w-4xl w-full overflow-hidden flex flex-col ${isAdmin ? 'h-[65vh]' : 'max-h-[95vh]'}`} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 bg-white border-b p-6 z-10">
                    <div className="flex justify-between items-start">
                        <div className="flex-1">
                            {/* Risk Badge */}
                            <div className="mb-3">
                                <span className="inline-flex items-center bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                                    {isNewRisk ? 'New Risk' : risk.riskId}
                                </span>
                            </div>

                            {/* Title */}
                            {isAdmin ? (
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="Enter risk title..."
                                    className="text-2xl font-semibold text-gray-900 w-full border-b-2 border-gray-300 focus:border-primary-600 focus:outline-none pb-1"
                                    required
                                />
                            ) : (
                                <h2 className="text-2xl font-semibold text-gray-900">
                                    {risk?.title}
                                </h2>
                            )}

                            {/* Status and Impact Row */}
                            <div className="flex items-center gap-3 mt-3">
                                <span className={`px-3 py-1 rounded text-sm font-medium ${
                                    risk?.signOff
                                        ? 'bg-green-100 text-green-700'
                                        : 'bg-amber-100 text-amber-700'
                                }`}>
                                    {risk?.signOff ? 'Signed Off' : 'Awaiting Sign-off'}
                                </span>

                                {isAdmin ? (
                                    <select
                                        name="impact"
                                        value={formData.impact}
                                        onChange={handleChange}
                                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                    >
                                        <option value="">Impact: Not set</option>
                                        <option value="High">Impact: High</option>
                                        <option value="Medium">Impact: Medium</option>
                                        <option value="Low">Impact: Low</option>
                                    </select>
                                ) : risk?.impact && (
                                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                                        risk.impact === 'High' ? 'bg-red-100 text-red-700' :
                                        risk.impact === 'Medium' ? 'bg-orange-100 text-orange-700' :
                                        'bg-blue-100 text-blue-700'
                                    }`}>
                                        {risk.impact} Impact
                                    </span>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors ml-4"
                        >
                            <FaTimes className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-6">
                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Description
                            </label>
                            {isAdmin ? (
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={4}
                                    placeholder="Describe the risk..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                                    required
                                />
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <p className="text-gray-700 whitespace-pre-wrap">{risk?.description}</p>
                                </div>
                            )}
                        </div>

                        {/* Assigned To */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Assigned For Sign-off
                            </label>
                            {isAdmin ? (
                                <>
                                    <select
                                        value={formData.assignedToEmail || ''}
                                        onChange={handleAssigneeChange}
                                        disabled={!!risk?.signOff}
                                        className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${risk?.signOff ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    >
                                        <option value="">Select team member...</option>
                                        {assignableMembers.map(member => (
                                            <option key={member.email} value={member.email} disabled={member.disabled === true}>
                                                {member.name} ({member.email}){member.disabled ? ' - disabled' : ''}
                                            </option>
                                        ))}
                                    </select>
                                    {risk?.signOff && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            Assignee cannot be changed while risk is signed off. Remove sign-off first.
                                        </p>
                                    )}
                                </>
                            ) : (
                                <p className="text-gray-700">{risk?.assignedToName || 'Not assigned'}</p>
                            )}
                        </div>

                        {/* Sign-off actions - only show when signed (for admin to remove) or for client to sign */}
                        {risk?.signOff && (
                            <div className="bg-gray-50 rounded-lg p-4">
                                <div className="text-sm text-gray-600 space-y-1">
                                    <p><span className="font-medium">Signed by:</span> {risk.signOff.signedByName}</p>
                                    <p><span className="font-medium">Date:</span> {formatDate(risk.signOff.signedAt)}</p>
                                    <p className="mt-2 italic text-gray-700">"{risk.signOff.acknowledgement}"</p>
                                </div>
                                {isAdmin && (
                                    <button
                                        type="button"
                                        onClick={() => setShowRemoveSignOffModal(true)}
                                        className="mt-3 text-sm text-red-600 hover:text-red-700 font-medium"
                                    >
                                        Remove Sign-off
                                    </button>
                                )}
                            </div>
                        )}
                        {risk && !risk.signOff && risk.assignedToEmail && !isAdmin && risk.assignedToEmail.toLowerCase() === (currentUserEmail || '').toLowerCase() && (
                            <div className="flex justify-center">
                                <button
                                    type="button"
                                    onClick={() => setShowSignOffModal(true)}
                                    className="px-6 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
                                >
                                    Sign Off
                                </button>
                            </div>
                        )}

                        {/* Sign-off History */}
                        {risk?.signOffHistory?.length > 0 && (
                            <div>
                                <button
                                    type="button"
                                    onClick={() => setShowHistory(!showHistory)}
                                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
                                >
                                    <FaHistory />
                                    Sign-off History ({risk.signOffHistory.length})
                                </button>
                                {showHistory && (
                                    <div className="mt-3 space-y-3">
                                        {risk.signOffHistory.map((entry, idx) => (
                                            <div key={idx} className="bg-gray-100 rounded-lg p-3 text-sm">
                                                <p className="font-medium text-gray-900">{entry.signedByName}</p>
                                                <p className="text-gray-600">Signed: {formatDate(entry.signedAt)}</p>
                                                <p className="text-gray-700 italic mt-1">"{entry.acknowledgement}"</p>
                                                <div className="mt-2 pt-2 border-t border-gray-200">
                                                    <p className="text-red-600">
                                                        Removed by {entry.removedByName} on {formatDate(entry.removedAt)}
                                                    </p>
                                                    <p className="text-gray-600">Reason: {entry.removalReason}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Metadata */}
                        {risk && (
                            <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
                                <p>Created by {risk.createdByName} on {formatDate(risk.createdAt)}</p>
                                {risk.updatedAt !== risk.createdAt && (
                                    <p>Last updated: {formatDate(risk.updatedAt)}</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
                    {isAdmin && risk && (
                        <button
                            type="button"
                            onClick={() => onDelete(risk.riskId)}
                            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors"
                        >
                            <FaTrash size={14} />
                            Delete
                        </button>
                    )}
                    <div className="flex-1" />
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium transition-colors"
                        >
                            {isAdmin ? 'Cancel' : 'Close'}
                        </button>
                        {isAdmin && (
                            <button
                                type="button"
                                onClick={hasChanges ? handleSubmit : onClose}
                                disabled={saving}
                                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {saving && (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                )}
                                {isNewRisk ? 'Create Risk' : (hasChanges ? 'Save Changes' : 'Close')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Sign-off Modal */}
            {showSignOffModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center" onClick={e => e.stopPropagation()}>
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowSignOffModal(false)} />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-xl w-full mx-4 p-6">
                        <h3 className="text-xl font-semibold mb-4">Sign Off on Risk</h3>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                            <div className="flex items-start gap-3">
                                <FaExclamationTriangle className="text-amber-500 mt-0.5 w-5 h-5 flex-shrink-0" />
                                <div>
                                    <p className="font-semibold text-amber-800">Important</p>
                                    <p className="text-sm text-amber-700 mt-1">
                                        By signing off this risk you acknowledge that you have attended a meeting with the Migration Experts team where this risk has been fully explained to you, and that you fully understand the risk.
                                    </p>
                                    <p className="text-sm text-amber-700 mt-2">
                                        If this is not the case then please cancel this window and ask the Migration Experts team for more understanding regarding the risk.
                                    </p>
                                </div>
                            </div>
                        </div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Your Acknowledgement
                        </label>
                        <textarea
                            value={acknowledgement}
                            onChange={(e) => setAcknowledgement(e.target.value)}
                            placeholder="Please provide your acknowledgement (minimum 10 characters)"
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-4 resize-none"
                        />
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowSignOffModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSignOff}
                                disabled={acknowledgement.trim().length < 10}
                                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Confirm Sign-off
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove Sign-off Modal */}
            {showRemoveSignOffModal && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center" onClick={e => e.stopPropagation()}>
                    <div className="absolute inset-0 bg-black/50" onClick={() => setShowRemoveSignOffModal(false)} />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                        <h3 className="text-xl font-semibold mb-4">Remove Sign-off</h3>
                        <p className="text-gray-600 mb-4">
                            This will remove the current sign-off and notify the assigned user that a new sign-off is required.
                        </p>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Reason for Removal
                        </label>
                        <textarea
                            value={removalReason}
                            onChange={(e) => setRemovalReason(e.target.value)}
                            placeholder="Please provide a reason for removing the sign-off"
                            rows={3}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-4 resize-none"
                        />
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowRemoveSignOffModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleRemoveSignOff}
                                disabled={!removalReason.trim()}
                                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Remove Sign-off
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RiskDetailsPanel;
